import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import {
  NonNullableFormBuilder,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import {
  BaseDialogComponent,
  ReusableDialogComponent,
  ButtonComponent,
  ErrorComponent,
  TypographyComponent,
  BreadcrumbListComponent,
  InputComponent,
  SearchInputComponent
} from '@hyperkit';
import type { BreadcrumbItem } from '@hyperkit';
import { PivotCardComponent } from './pivot-card/pivot-card.component';
import { debounceTime } from 'rxjs/operators';
// import { PivotGeneratedContent } from '../../../../pages/hunt/types';
import { PivotGeneratedContent } from '@cyborg-monorepo/hunter/common';
import { generateQueryString, generatePivotQuery } from './helper';
import { Enums } from '@cyborg-monorepo/hunter/common';
import { HuntContextService } from '../../../../pages/hunt/services/hunt-context.service';

export type PivotItemSource = 'auto' | 'demo' | 'recommended';
export interface PivotItem {
  id: number;
  title: string;
  description: string;
  source?: PivotItemSource;
}

@Component({
  standalone: true,
  selector: 'capture-data-dialog',
  templateUrl: './capture-data-dialog.component.html',
  styleUrls: ['./capture-data-dialog.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    TypographyComponent,
    ErrorComponent,
    BreadcrumbListComponent,
    PivotCardComponent,
    InputComponent,
    ReusableDialogComponent,
    SearchInputComponent
  ],
})
export class CaptureDataDialogComponent extends BaseDialogComponent<boolean> {
  private fb = inject(NonNullableFormBuilder);
  private cdr = inject(ChangeDetectorRef);

  private hunt = inject(HuntContextService)

  public error = signal<string>('');
  public captureData = signal<boolean>(false);
  public pivotSearchTerm = signal('');

  readonly selectedEnvironment = this.hunt.editor.selectedEnvironment;
  // readonly selectedQuery = this.hunt.queryLogic.selectedQuery;
  readonly selectedQueryIndex = this.hunt.queryLogic.selectedQueryIndex;
  readonly selectedTool = this.hunt.queryLogic.selectedTool;
  

  
  public filteredPivotItems = signal<PivotItem[]>([]);
  public selectedPivotItems = signal<Set<number>>(new Set());
  
  public isDemo = true;

  public demoPivotItems = signal<PivotItem[]>([{
    id: 1,
    title: 'Parent-Child Process Relationship',
    description: 'This query helps pivot based on child/parent process name discovered during investigation.',
    source: 'demo'
  }, {
    id: 2,
    title: 'Child - Parent - GrandParent ProcessID',
    description: 'This enables pivoting using child, parent, or grandparent process names from artifacts.',
    source: 'demo'
  }, {
    id: 3,
    title: 'Parent-Child ProcessID Relationship',
    description: 'Use parent or child process ID to continue the investigation.',
    source: 'demo'
  }]);

  // public recommendedPivotItems = signal<PivotItem[]>([{
  //   id: 11,
  //   title: 'Parent-Child Process Relationship',
  //   description: 'This query helps pivot based on child/parent process name discovered during investigation.',
  //   source: 'recommended'
  // }, {
  //   id: 12,
  //   title: 'Child - Parent - GrandParent ProcessID',
  //   description: 'This enables pivoting using child, parent, or grandparent process names from artifacts.',
  //   source: 'recommended'
  // }, {
  //   id: 13,
  //   title: 'Parent-Child ProcessID Relationship',
  //   description: 'Use parent or child process ID to continue the investigation.',
  //   source: 'recommended'
  // }]);

  public autoGeneratedItems = signal<PivotItem[]>([]);


  public breadcrumbs = signal<(BreadcrumbItem & { step: 'capture' | 'pivot' })[]>([
    { text: 'Specify Your Capture Data', selected: true, step: 'capture' },
    { text: 'Choose Your Pivot', selected: false, step: 'pivot' },
  ]);


  public onContinue(): void {
    this.captureData.set(true);
    this.updateBreadcrumbs('pivot');
  }

  public goBack(): void {
    this.captureData.set(false);
    this.updateBreadcrumbs('capture');
  }

  public onSkip(): void {
    this.captureData.set(true);
    this.updateBreadcrumbs('pivot');
  }


  public pivotItems = computed(() => [
    // ...this.recommendedPivotItems(),
    ...this.demoPivotItems(),
    // ...this.autoGeneratedItems(),
  ]);

  public onPivotSelection(item: PivotItem): void {
    const current = new Set(this.selectedPivotItems());
    current.has(item.id) ? current.delete(item.id) : current.add(item.id);
    this.selectedPivotItems.set(current);
  }

  constructor() {
    super();

    effect(() => {
      const query = this.pivotSearchTerm().toLowerCase().trim();
      const allItems = this.pivotItems();
      const filtered = !query ? allItems : allItems.filter(item =>
        item.title.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query));
      this.filteredPivotItems.set(filtered);
    });

    effect(() => {
      console.log('🔁 selected enviornment:', this.selectedEnvironment());
      console.log('🔁 selected tool:', this.selectedTool());
      // console.log('🔁 selected query:', this.selectedQuery());
      console.log('🔁 selected query:', this.selectedQueryIndex());
    });


    effect(() => {
      console.log('🔁 demoPivotItems:', this.demoPivotItems());
    });

    // effect(() => {
    //   console.log('🔁 recommendedPivotItems:', this.recommendedPivotItems());
    // });

    effect(() => {
      console.log('🔁 autoGeneratedItems:', this.autoGeneratedItems());
    });

    effect(() => {
      console.log('🔁 filteredPivotItems:', this.filteredPivotItems());
    });

    effect(() => {
      console.log('✅ selectedPivotItems:', Array.from(this.selectedPivotItems()));
    });

    this.dataCaptureForm.valueChanges.pipe(debounceTime(300)).subscribe(() => {
      if (this.dataCaptureForm.valid) {
        const pivots = this.generatePivotItemsFromForm();
        this.autoGeneratedItems.set(pivots);
      }
    });
  }

  public dataCaptureForm = this.fb.group({
    hostname: ['', Validators.required],
    processName: ['', Validators.required],
    processID: ['', Validators.required],
    parentProcessName: ['', Validators.required],
    parentProcessID: ['', Validators.required],
  });

  // public fillFormForDemo(): void {
  //   const hostnames = ['DESKTOP-ACME01', 'LAPTOP-DELTA02', 'CORP-SERVER-03', 'WIN10-INVEST01'];
  //   const processes = ['svchost.exe', 'powershell.exe', 'explorer.exe', 'cmd.exe', 'chrome.exe'];
  //   const parentProcesses = ['services.exe', 'wininit.exe', 'System', 'taskhostw.exe'];
  //   const random = (arr: string[]) => arr[Math.floor(Math.random() * arr.length)];

  //   this.dataCaptureForm.setValue({
  //     hostname: random(hostnames),
  //     processName: random(processes),
  //     processID: String(Math.floor(1000 + Math.random() * 9000)),
  //     parentProcessName: random(parentProcesses),
  //     parentProcessID: String(Math.floor(1000 + Math.random() * 9000))
  //   });
  // }


  public updateBreadcrumbs(step: 'capture' | 'pivot'): void {
    const updated = this.breadcrumbs().map(crumb => ({
      ...crumb,
      selected: crumb.step === step
    }));
    this.breadcrumbs.set(updated);
  }

  public onBreadcrumbClick(index: number): void {
    const item = this.breadcrumbs()[index];
    if (item.step === 'capture') this.goBack();
    if (item.step === 'pivot') this.onContinue();
  }

  public generatePivotItemsFromForm(): PivotItem[] {
    const formValues = this.dataCaptureForm.getRawValue();
    const fields = Object.entries(formValues)
      .filter(([_, val]) => !!val)
      .map(([key, val]) => ({ key, value: val }));
    const totalToGenerate = Math.floor(Math.random() * 21) + 10;

    const actionTemplates = [
      'Search for activity involving {key}',
      'Trace child processes of {key}',
      'Find logs related to {value}',
      'Correlate {key} with known bad behavior',
      'Audit execution of {value}',
      'Check registry keys for {value}',
      'Analyze network connections from {value}',
      'Investigate command line usage for {value}',
      'Flag abnormal usage of {key}',
      'Look for persistence mechanisms involving {value}'
    ];

    const results: PivotItem[] = [];
    for (let i = 0; i < totalToGenerate; i++) {
      const field = fields[i % fields.length];
      const id = 100 + i;
      const template = actionTemplates[Math.floor(Math.random() * actionTemplates.length)];
      const title = template.replace('{key}', field.key).replace('{value}', field.value);
      results.push({
        id,
        title,
        description: `Generated pivot for '${field.key}' = '${field.value}'.`,
        source: 'auto'
      });
    }
    return results;
  }

  public trackById = (_: number, item: PivotItem) => item.id;

  protected override initializeOptions(): void {
    this.options = {
      headerText: 'Apply Pivot Queries'
    };
  }

  public togglePivotSelection(item: PivotItem): void {
    const current = new Set(this.selectedPivotItems());
    if (current.has(item.id)) {
      current.delete(item.id);
    } else {
      current.add(item.id);
    }
    this.selectedPivotItems.set(current);
  }
  
  

  public onSubmit(): void {
    const form = this.dataCaptureForm.getRawValue();
    const fields = Object.values(form).filter(Boolean);
    const missingFields = Object.entries(form).filter(([_, val]) => !val).map(([key]) => key);

    const selected = this.pivotItems().filter(p => this.selectedPivotItems().has(p.id));

    const generatedContent: PivotGeneratedContent[] = selected.map(pivot => ({
      goal: 'Hunt',
      title: pivot.title,
      query: generatePivotQuery(pivot.title),
      matched: true,
      category: pivot.source,
      missingFields,
      deployment_requirements: {
        categories: ['endpoint:sysmon'],
        searchable_data: fields,
        deployment_steps: 'This was generated dynamically from your input.'
      },
      notes: '',
      status: this.getQueryStatus(),
      pivots: [{
        id: pivot.id,
        source: pivot.source,
        reference: pivot.title
      }]
    }));

    console.log('🧠 Generated content:', generatedContent);

      //selected package id
      const selectedPkgId = this.hunt.editor.activePackage().UUID;
      if (!selectedPkgId) {
        console.error('❌ No active hunt package found. Cannot append pivots.');
        return;
      }
      
      // selected analysis id 
      const selectedEnvironmentId = this.selectedEnvironment().id
      // selected tool id 
      const selectedToolId = this.selectedTool().id;
      // selected query id
      const selectedQueryId = this.selectedQueryIndex();

      const tool = this.hunt.editor.getTool(selectedPkgId, selectedToolId)();
       if (!tool) return;

      const huntQueries = [...(tool.generatedContent?.Hunt ?? [])];

      // ensure index exists
      if (!huntQueries[selectedQueryId]) {
        console.warn(`❌ No Hunt query at index ${selectedQueryId}`);
        return;
      }

      // patch the pivots into the specific query
      huntQueries[selectedQueryId] = {
        ...huntQueries[selectedQueryId],
        pivots: generatedContent
      };

      // apply the patch
      this.hunt.editor.appendPivotsToQueries(
        selectedPkgId,
        selectedEnvironmentId,
        selectedToolId,
        selectedQueryId,
        generatedContent
      );
      // ✅ Force signal rehydration
      this.hunt.editor.rehydrate();
      this.hunt.editor.triggerManualUpdate();


                  
      console.log('✅ Added pivot queries to active package:', generatedContent);
      this.dialogRef.close();
  
}

  getQueryStatus(): `${Enums.QueryStatusEnum}` {
    const values: `${Enums.QueryStatusEnum}`[] = [
      Enums.QueryStatusEnum.TO_DO,
      Enums.QueryStatusEnum.SKIP,
      Enums.QueryStatusEnum.RUN
    ];
    return values[Math.floor(Math.random() * values.length)];
  }
}
